import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { type Filter, type TableFilter, validation } from '@ipfabric/utils';
import { produce } from 'immer';
import {
  type AllowedOperator,
  getDefaultOperator,
  getOperatorFromReadable,
} from './filterOperators';
import { getOperatorFromFilter } from './getOperatorFromFilter';

const transformFilter = (filter: Filter, type: TableFilter['type']): Filter => {
  if (type === 'ip' && filter[0] === 'like') {
    // ipv4 valid CIDR without operator - use `cidr` operator
    const validated = validation.cidr.validate(filter[1]);
    if (validated.error) {
      return filter;
    }
    return ['cidr', filter[1]];
  }
  if (type === 'ipv6' && filter[0] === 'like') {
    // ipv6 valid CIDR without operator - use `cidr` operator
    const validated = validation.cidrIpv6.validate(filter[1]);
    if (validated.error) {
      return filter;
    }
    return ['cidr', filter[1]];
  }
  if (type === 'boolean' && filter[1] === 'true') {
    // parse boolean value from filter string
    return produce(filter, (draft) => {
      draft[1] = true;
    });
  }
  if (type === 'boolean' && filter[1] === 'false') {
    return produce(filter, (draft) => {
      draft[1] = false;
    });
  }
  return filter;
};

export const getOperatorAndValueFromReadable = (
  filter: TableFilter,
  // string[] type is passed there from SimpleFilterField.value in many usages, but in none of them it should appear
  // when it reaches this function (mutliselect has special handling). So we allow it in types and stumble if it
  // sneaks in there.
  value: string | string[] | null,
): [operator: AllowedOperator | undefined, filterValue: string] | null => {
  if (Array.isArray(value)) {
    stumble('Cannot convert array of strings to simple Filter.');
    return null;
  }
  if (value === null || value === '') {
    return null;
  }

  const readableOperator = getOperatorFromFilter(filter, value);
  const operator = getOperatorFromReadable(readableOperator);
  const filterValue = value.substring(readableOperator.length);
  return [operator, filterValue];
};

export const getFilterFromReadable = (
  filter: TableFilter,
  // string[] type is passed there from SimpleFilterField.value in many usages, but in none of them it should appear
  // when it reaches this function (mutliselect has special handling). So we allow it in types and stumble if it
  // sneaks in there.
  value: string | string[] | null,
): Filter | null => {
  const operatorAndValue = getOperatorAndValueFromReadable(filter, value);
  if (operatorAndValue === null) {
    return null;
  }
  const [operator, filterValue] = operatorAndValue;

  return transformFilter(
    operator
      ? [operator, filterValue]
      : [getDefaultOperator(filter, filterValue), filterValue],
    filter.type,
  );
};
